/**
 * Price alerts.
 *
 * Alerts fire on a **crossing**, not on a comparison. The difference matters:
 * "price >= 70,000" is true the moment you create the alert if price is
 * already above 70,000, so a naive comparison fires instantly on every alert
 * you set below the market and never fires again. Tracking the previous print
 * and requiring the threshold to be traversed is what makes an alert mean
 * "tell me when this happens" rather than "tell me it already happened".
 *
 * Evaluation runs on the raw trade stream — hundreds of calls a second — so
 * the hot path is a Map lookup and a numeric compare, with the per-symbol
 * alert lists pre-bucketed rather than filtered on each print.
 */

import { Emitter } from '../lib/emitter';
import { DEMO } from './mode';

export type AlertDirection = 'above' | 'below';
export type AlertStatus = 'armed' | 'triggered';

export interface Alert {
  id: string;
  symbol: string;
  price: number;
  direction: AlertDirection;
  status: AlertStatus;
  createdAt: number;
  triggeredAt: number | null;
  /** Price at the moment of firing, which may overshoot the threshold. */
  triggeredPrice: number | null;
  note: string;
  /** Re-arm automatically after firing. */
  repeat: boolean;
  sound: boolean;
}

export interface AlertEvent {
  alert: Alert;
  price: number;
  at: number;
}

const LS_KEY = DEMO ? 'terminal.demo.alerts.v1' : 'terminal.alerts.v1';

let counter = 0;
const newId = () => `a${Date.now().toString(36)}${(counter++).toString(36)}`;

class AlertEngine {
  private alerts: Alert[] = [];
  /** symbol -> armed alerts, rebuilt whenever the list changes. */
  private buckets = new Map<string, Alert[]>();
  /** Last observed price per symbol, for crossing detection. */
  private lastPrice = new Map<string, number>();

  /** Fires when an alert triggers. The UI shows a toast; audio plays here. */
  readonly triggered = new Emitter<AlertEvent>();
  /** Fires when the list itself changes, so React can re-render. */
  readonly changed = new Emitter<void>();

  version = 0;

  constructor() {
    this.alerts = this.load();
    this.reindex();
  }

  // -- persistence ----------------------------------------------------------

  private load(): Alert[] {
    try {
      const raw = localStorage.getItem(LS_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw) as Alert[];
      if (!Array.isArray(parsed)) return [];
      return parsed
        .filter((a) => a && typeof a.symbol === 'string' && Number.isFinite(a.price))
        .slice(0, 200);
    } catch {
      return [];
    }
  }

  private save(): void {
    try {
      localStorage.setItem(LS_KEY, JSON.stringify(this.alerts));
    } catch {
      /* quota */
    }
  }

  private reindex(): void {
    this.buckets.clear();
    for (const a of this.alerts) {
      if (a.status !== 'armed') continue;
      let list = this.buckets.get(a.symbol);
      if (!list) {
        list = [];
        this.buckets.set(a.symbol, list);
      }
      list.push(a);
    }
    this.version++;
  }

  private commit(): void {
    this.reindex();
    this.save();
    this.changed.emit();
  }

  // -- queries --------------------------------------------------------------

  list(): readonly Alert[] {
    return this.alerts;
  }

  forSymbol(symbol: string): Alert[] {
    return this.alerts.filter((a) => a.symbol === symbol);
  }

  get armedCount(): number {
    let n = 0;
    for (const a of this.alerts) if (a.status === 'armed') n++;
    return n;
  }

  // -- mutations ------------------------------------------------------------

  /**
   * Create an alert. Direction is inferred from where the price sits relative
   * to the market right now, which is what the user means 100% of the time.
   */
  add(symbol: string, price: number, opts: Partial<Alert> = {}): Alert {
    const current = this.lastPrice.get(symbol);
    const inferred: AlertDirection =
      opts.direction ?? (current !== undefined && price < current ? 'below' : 'above');

    const alert: Alert = {
      id: newId(),
      symbol,
      price,
      direction: inferred,
      status: 'armed',
      createdAt: Date.now(),
      triggeredAt: null,
      triggeredPrice: null,
      note: opts.note ?? '',
      repeat: opts.repeat ?? false,
      sound: opts.sound ?? true,
    };
    this.alerts = [alert, ...this.alerts].slice(0, 200);
    this.commit();
    return alert;
  }

  remove(id: string): void {
    this.alerts = this.alerts.filter((a) => a.id !== id);
    this.commit();
  }

  update(id: string, patch: Partial<Alert>): void {
    this.alerts = this.alerts.map((a) => (a.id === id ? { ...a, ...patch } : a));
    this.commit();
  }

  rearm(id: string): void {
    this.alerts = this.alerts.map((a) =>
      a.id === id
        ? { ...a, status: 'armed' as const, triggeredAt: null, triggeredPrice: null }
        : a,
    );
    this.commit();
  }

  clearTriggered(): void {
    this.alerts = this.alerts.filter((a) => a.status === 'armed');
    this.commit();
  }

  clearAll(): void {
    this.alerts = [];
    this.commit();
  }

  // -- evaluation -----------------------------------------------------------

  /**
   * Called for every trade print and ticker update. Must stay cheap: with
   * eight symbols this runs several hundred times a second.
   */
  onPrice(symbol: string, price: number): void {
    if (!Number.isFinite(price)) return;
    const prev = this.lastPrice.get(symbol);
    this.lastPrice.set(symbol, price);

    const list = this.buckets.get(symbol);
    if (!list || list.length === 0) return;
    // The very first print for a symbol has nothing to cross from.
    if (prev === undefined) return;

    let fired = false;
    for (let i = 0; i < list.length; i++) {
      const a = list[i];
      if (a.status !== 'armed') continue;
      const crossedUp = a.direction === 'above' && prev < a.price && price >= a.price;
      const crossedDown = a.direction === 'below' && prev > a.price && price <= a.price;
      if (!crossedUp && !crossedDown) continue;

      a.triggeredAt = Date.now();
      a.triggeredPrice = price;
      if (!a.repeat) a.status = 'triggered';
      fired = true;

      this.triggered.emit({ alert: a, price, at: a.triggeredAt });
      if (a.sound) playChime(a.direction);
      notify(a, price);
    }

    if (fired) {
      // Rebuild buckets so disarmed alerts stop being evaluated.
      this.alerts = [...this.alerts];
      this.commit();
    }
  }

  /** Seed the reference price without evaluating, e.g. on first load. */
  seed(symbol: string, price: number): void {
    if (Number.isFinite(price) && !this.lastPrice.has(symbol)) {
      this.lastPrice.set(symbol, price);
    }
  }

  priceOf(symbol: string): number | undefined {
    return this.lastPrice.get(symbol);
  }
}

// ---------------------------------------------------------------------------
// Output channels
// ---------------------------------------------------------------------------

let audioCtx: AudioContext | null = null;

/**
 * A short two-note chime, synthesised rather than loaded, so there is no asset
 * to ship and no fetch to fail. Rising for an upside cross, falling for a
 * downside one — you can tell which way the market went without looking.
 */
function playChime(direction: AlertDirection): void {
  try {
    if (!audioCtx) {
      const Ctor =
        window.AudioContext ??
        (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!Ctor) return;
      audioCtx = new Ctor();
    }
    // Browsers suspend the context until a user gesture; alerts are created by
    // one, so this normally succeeds immediately.
    if (audioCtx.state === 'suspended') void audioCtx.resume();

    const now = audioCtx.currentTime;
    const notes = direction === 'above' ? [660, 990] : [560, 380];
    notes.forEach((freq, i) => {
      const osc = audioCtx!.createOscillator();
      const gain = audioCtx!.createGain();
      osc.type = 'triangle';
      osc.frequency.value = freq;
      const t0 = now + i * 0.13;
      gain.gain.setValueAtTime(0.0001, t0);
      gain.gain.exponentialRampToValueAtTime(0.13, t0 + 0.012);
      gain.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.24);
      osc.connect(gain).connect(audioCtx!.destination);
      osc.start(t0);
      osc.stop(t0 + 0.26);
    });
  } catch {
    /* audio is a nicety, never a failure */
  }
}

function notify(a: Alert, price: number): void {
  try {
    if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
    new Notification(`${a.symbol} ${a.direction} ${a.price}`, {
      body: `Traded at ${price}${a.note ? ` — ${a.note}` : ''}`,
      tag: a.id,
      silent: true,
    });
  } catch {
    /* notifications are optional */
  }
}

export function requestNotificationPermission(): void {
  try {
    if (typeof Notification === 'undefined') return;
    if (Notification.permission === 'default') void Notification.requestPermission();
  } catch {
    /* ignore */
  }
}

export const alertEngine = new AlertEngine();
